import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Heart, MessageCircle, LineChart, Clock } from 'lucide-react';
import * as api from '../../api/client';
import { useInstagramAccounts } from '../../context/InstagramAccountContext';
import PageHeader from '../components/PageHeader';
import NoAccountState from '../components/NoAccountState';

const STAT_COLORS = ['text-lc-pink', 'text-lc-purple', 'text-lc-orange', 'text-[#34e2d1]'];

export default function MetricsPage() {
  const { t, i18n } = useTranslation();
  const { activeAccount } = useInstagramAccounts();
  const [summary, setSummary] = useState<api.AccountMetricsSummary | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!activeAccount) {
      setSummary(null);
      return;
    }
    setLoading(true);
    setSummary(null);
    try {
      setSummary(await api.getAccountMetricsSummary(activeAccount.id));
    } finally {
      setLoading(false);
    }
  }, [activeAccount]);

  useEffect(() => {
    load();
  }, [load]);

  if (!activeAccount) {
    return <NoAccountState />;
  }

  return (
    <div>
      <PageHeader icon={LineChart} title={t('metrics.title')} subtitle={t('metrics.subtitle')} />

      {loading || !summary ? (
        <p className="text-gray-400 dark:text-lc-faint text-sm">{t('common.loading')}</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <StatCard label={t('metrics.totalLikes')} value={summary.totalLikes} locale={i18n.language} color={STAT_COLORS[0]} />
            <StatCard label={t('metrics.totalComments')} value={summary.totalComments} locale={i18n.language} color={STAT_COLORS[1]} />
            <StatCard label={t('metrics.totalReach')} value={summary.totalReach} locale={i18n.language} color={STAT_COLORS[2]} />
            <StatCard label={t('metrics.totalImpressions')} value={summary.totalImpressions} locale={i18n.language} color={STAT_COLORS[3]} />
          </div>

          <div className="card p-5 mb-8">
            <div className="flex items-start justify-between gap-2 mb-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-[#7a6b5d] dark:text-lc-faint">
                {t('metrics.bestHour')}
              </p>
              {summary.isDefaultBestHour ? (
                <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#34e2d1]/15 text-[#1a9e92] dark:text-[#34e2d1]">
                  {t('metrics.bestHourDefaultBadge')}
                </span>
              ) : null}
            </div>
            <p className="text-[28px] font-extrabold text-[#34e2d1]">{api.formatBestHour(summary)}</p>
            <p className="mt-1 text-xs text-[#7a6b5d] dark:text-lc-muted flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              {summary.isDefaultBestHour
                ? t('metrics.bestHourDefaultHint')
                : t('metrics.bestHourDataHint')}
            </p>
          </div>

          <div className="card p-6">
            <h2 className="text-base font-bold text-[#241c14] dark:text-lc-text mb-5">{t('metrics.topPostsTitle')}</h2>
            {summary.topPosts.length === 0 ? (
              <p className="text-gray-400 dark:text-lc-faint text-sm">{t('metrics.noData')}</p>
            ) : (
              <div className="flex flex-col gap-3">
                {summary.topPosts.map((p) => (
                  <div
                    key={p.scheduledPostId}
                    className="p-3 rounded-lg bg-white dark:bg-lc-bg border border-[#e8ddd0] dark:border-lc-border flex items-center justify-between text-sm"
                  >
                    <div>
                      <div className="text-xs font-semibold text-[#241c14] dark:text-lc-text mb-1">
                        {t('metrics.postLabel', { id: p.scheduledPostId.slice(0, 8) })}
                      </div>
                      <div className="text-[11px] text-[#7a6b5d] dark:text-lc-faint flex items-center gap-3">
                        <span className="flex items-center gap-1"><Heart className="h-3 w-3" /> {p.likes}</span>
                        <span className="flex items-center gap-1"><MessageCircle className="h-3 w-3" /> {p.comments}</span>
                      </div>
                    </div>
                    <div className="text-[13px] font-bold text-lc-pink">{p.engagementScore}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, locale, color }: { label: string; value: number; locale: string; color: string }) {
  return (
    <div className="card p-5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-[#7a6b5d] dark:text-lc-faint mb-3">{label}</p>
      <p className={`text-[28px] font-extrabold ${color}`}>{value.toLocaleString(locale)}</p>
    </div>
  );
}
